/**
 * OS's web-only state: where it sits (floating, the side sheet or the full
 * page; web-redesign-08-os.md, Decided 4 and 5), whether it is open over the
 * page and which thread it holds, whether it is minimised (every shape: the
 * floating panel, the side sheet, or the full page sent to the page under it)
 * and where its pill stands, a count bumped whenever a turn lands so every
 * list of past conversations reads the server again, and what the floating
 * Ask button says while OS is out of sight (a turn still working, a call
 * still up). None of it exists on the phone, whose sheet is the only surface.
 *
 * The layout and the pill's corner are kept per person, per browser
 * (`os-place-model.ts`), so OS opens next time where it was left; the rest
 * never survives a reload.
 */
import { create } from 'zustand';

import { FIRST_WHERE, HOME_CORNER, readWhereByPerson, whereFor, type OsLayout, type OsWhere, type PillCorner } from './os-place-model';

const WHERE_KEY = 'alluzo.os-where';

/** How long an ask for live mode from the Ask OS orb or ⌘J waits for a composer to take it: a route change with its page's first read. */
export const TALK_LIVE_ASK_MS = 5000;

const readKept = (): string | null => {
  try {
    return localStorage.getItem(WHERE_KEY);
  } catch {
    return null;
  }
};

const keep = (userId: string | null, where: OsWhere): void => {
  if (userId === null) return;
  try {
    localStorage.setItem(WHERE_KEY, JSON.stringify({ ...readWhereByPerson(readKept()), [userId]: where }));
  } catch {
    /* The choice holds for this tab. */
  }
};

type CoachState = {
  /** Whose choice `layout` and `corner` are, once the session is known. */
  userId: string | null;
  /** Where OS opens: floating, beside the page, or the full OS page. */
  layout: OsLayout;
  /** The corner the folded pill sits in. */
  corner: PillCorner;
  /** OS is open over the page (floating or beside it). The full page is a route, not this. */
  dockOpen: boolean;
  /**
   * OS is minimised (C3, C13): its frame stays mounted out of sight, so a
   * conversation or live mode goes on, and the minimised pill stands for it.
   */
  minimised: boolean;
  /** Bumped by Keyboard on the minimised pill: the composer opens its input row once, focused. */
  keyboardAsk: number;
  /** The thread OS holds over the pages (in `coach-chats`), or null before its first open. */
  dockThreadId: string | null;
  /** The two faces: the conversation, or the list of past ones. */
  dockView: 'thread' | 'history';
  /** The last app page outside the OS page, where Floating or Side sheet goes back to from the full page. */
  lastAppPath: string | null;
  /** Bumped when a turn finishes, so the lists of threads are read again. */
  revision: number;
  /** The threads with a turn in flight, read from each thread's own chat (`thread-busy`), shown or not. */
  busy: ReadonlySet<string>;
  /** When the call now up began (ms since the epoch), or null with no call. */
  callSince: number | null;
  /** A call that ended (Stop live) still owes its thread an answer: OS stays mounted out of sight until it lands. */
  callOwed: boolean;
  /** A thread whose call starts as soon as its composer is drawn: Talk live from the Dashboard's door. Taken once. */
  liveOnOpen: string | null;
  /**
   * When the Ask OS orb or ⌘J asked for live mode (C12, J31): the composer OS
   * shows next takes it once and starts Talk live. An ask nobody takes in
   * `TALK_LIVE_ASK_MS` lapses, so it never starts a call later by surprise.
   */
  talkLiveAskedAt: number | null;
  /**
   * How many AI-first pages are drawn (web-redesign-10 Decided 1): OS is the
   * page there, so Ask OS hides and nothing floats over it.
   */
  osOnPage: number;
  /**
   * How many pages keep OS beside them in the side sheet (an opened insight,
   * web-redesign-10 Decided 5), whatever layout the person kept; it is not
   * kept as their choice.
   */
  besideHere: number;
  /** Reads where this person left OS in this browser. */
  adoptPerson: (userId: string | null) => void;
  /** Keeps the layout, for next time. */
  setLayout: (layout: OsLayout) => void;
  /** Moves the folded pill, and keeps the corner. */
  setCorner: (corner: PillCorner) => void;
  setDockOpen: (open: boolean) => void;
  /** Minimize: OS goes out of sight to its pill, at the bottom right; live mode carries on. */
  minimise: () => void;
  /** Opens OS again on the same thread, at the bottom right. */
  expand: () => void;
  /** Opens OS again with the live pill's input row open and focused (Keyboard on the minimised pill). */
  expandWithKeyboard: () => void;
  setDockThread: (id: string) => void;
  /** Open OS on this thread, wherever the person is. */
  openDockOn: (id: string) => void;
  setDockView: (view: 'thread' | 'history') => void;
  setLastAppPath: (path: string) => void;
  bump: () => void;
  setThreadBusy: (id: string, busy: boolean) => void;
  setCall: (up: boolean) => void;
  setCallOwed: (owed: boolean) => void;
  /** Asks the composer of this thread to start the call once it is drawn. */
  callOnOpen: (id: string) => void;
  /** Takes the ask for this thread, once: true when the call should start now. */
  takeLiveOnOpen: (id: string) => boolean;
  /** The Ask OS orb or ⌘J: the next composer shown starts live mode. */
  askTalkLive: () => void;
  /** Takes that ask, once, while it is fresh: true when live mode should start now. */
  takeTalkLive: () => boolean;
  /** An AI-first page is drawn. The returned function says it went. */
  holdOsOnPage: () => () => void;
  /** A page that keeps OS beside it is drawn. The returned function says it went. */
  holdBeside: () => () => void;
};

export const useCoachStore = create<CoachState>((set, get) => ({
  userId: null,
  layout: FIRST_WHERE.layout,
  corner: FIRST_WHERE.corner,
  dockOpen: false,
  minimised: false,
  keyboardAsk: 0,
  dockThreadId: null,
  dockView: 'thread',
  lastAppPath: null,
  revision: 0,
  busy: new Set(),
  callSince: null,
  callOwed: false,
  liveOnOpen: null,
  talkLiveAskedAt: null,
  osOnPage: 0,
  besideHere: 0,
  adoptPerson: (userId) => {
    if (userId === get().userId) return;
    const where = whereFor(readWhereByPerson(readKept()), userId);
    set({ userId, layout: where.layout, corner: where.corner });
  },
  setLayout: (layout) => {
    keep(get().userId, { layout, corner: get().corner });
    // The person's own pick wins over a page that kept OS beside it, for the rest of the visit.
    set({ layout, besideHere: 0 });
  },
  setCorner: (corner) => {
    keep(get().userId, { layout: get().layout, corner });
    set({ corner });
  },
  setDockOpen: (open) => set({ dockOpen: open, minimised: false, dockView: 'thread' }),
  minimise: () => {
    keep(get().userId, { layout: get().layout, corner: HOME_CORNER });
    set({ minimised: true, corner: HOME_CORNER });
  },
  expand: () => set({ minimised: false }),
  expandWithKeyboard: () => set((state) => ({ minimised: false, dockView: 'thread', keyboardAsk: state.keyboardAsk + 1 })),
  setDockThread: (id) => set({ dockThreadId: id, dockView: 'thread' }),
  openDockOn: (id) => set({ dockOpen: true, minimised: false, dockThreadId: id, dockView: 'thread' }),
  setDockView: (view) => set({ dockView: view }),
  setLastAppPath: (path) => {
    if (get().lastAppPath !== path) set({ lastAppPath: path });
  },
  bump: () => set((state) => ({ revision: state.revision + 1 })),
  setThreadBusy: (id, busy) =>
    set((state) => {
      if (state.busy.has(id) === busy) return state;
      const next = new Set(state.busy);
      if (busy) next.add(id);
      else next.delete(id);
      return { busy: next };
    }),
  setCall: (up) => set((state) => (up === (state.callSince !== null) ? state : { callSince: up ? Date.now() : null })),
  setCallOwed: (owed) => {
    if (get().callOwed !== owed) set({ callOwed: owed });
  },
  callOnOpen: (id) => set({ liveOnOpen: id }),
  takeLiveOnOpen: (id) => {
    if (get().liveOnOpen !== id) return false;
    set({ liveOnOpen: null });
    return true;
  },
  askTalkLive: () => set({ talkLiveAskedAt: Date.now() }),
  takeTalkLive: () => {
    const at = get().talkLiveAskedAt;
    if (at === null) return false;
    set({ talkLiveAskedAt: null });
    return Date.now() - at <= TALK_LIVE_ASK_MS;
  },
  holdOsOnPage: () => {
    set((state) => ({ osOnPage: state.osOnPage + 1 }));
    let held = true;
    return () => {
      if (!held) return;
      held = false;
      set((state) => ({ osOnPage: Math.max(0, state.osOnPage - 1) }));
    };
  },
  holdBeside: () => {
    set((state) => ({ besideHere: state.besideHere + 1 }));
    let held = true;
    return () => {
      if (!held) return;
      held = false;
      set((state) => ({ besideHere: Math.max(0, state.besideHere - 1) }));
    };
  },
}));
